import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { ListItemText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Location data structure
const locationData = {
  zones: ['North', 'East', 'West', 'South'],
  states: {
    North: ['Delhi', 'Haryana', 'Punjab'],
    East: ['West Bengal', 'Bihar', 'Odisha'],
    West: ['Maharashtra', 'Gujarat', 'Rajasthan'],
    South: ['Karnataka', 'Tamil Nadu', 'Kerala']
  },
  cities: {
    Delhi: ['New Delhi', 'Delhi NCR'],
    Haryana: ['Gurgaon', 'Faridabad', 'Noida'],
    Maharashtra: ['Mumbai', 'Pune', 'Nagpur'],
    Karnataka: ['Bangalore', 'Mysore'],
    'Tamil Nadu': ['Chennai', 'Coimbatore']
  },
  stores: {
    'New Delhi': ['Store 1', 'Store 2', 'Store 3'],
    Mumbai: ['Store 4', 'Store 5', 'Store 6'],
    Bangalore: ['Store 7', 'Store 8', 'Store 9'],
    Chennai: ['Store 10', 'Store 11', 'Store 12']
  }
};

// Inside your LocationSelector component, before your other state declarations:

const LocationSelector = ({ onSelectionChange, initialSelection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [selection, setSelection] = useState(
    initialSelection || {
      zones: [],
      states: [],
      cities: [],
      stores: []
    }
  );

  useEffect(() => {
    onSelectionChange(selection);
  }, [selection, onSelectionChange]);

  const getAvailableItems = (type) => {
    switch (type) {
      case 'zones':
        return locationData.zones;
      case 'states':
        if (selection.zones.length === 0) return [];
        return selection.zones.flatMap((zone) => locationData.states[zone] || []);
      case 'cities':
        if (selection.states.length === 0) return [];
        return selection.states.flatMap((state) => locationData.cities[state] || []);
      case 'stores':
        if (selection.cities.length === 0) return [];
        return selection.cities.flatMap((city) => locationData.stores[city] || []);
      default:
        return [];
    }
  };

  const handleSelect = (type, value) => {
    setSelection((prev) => {
      const newSelection = {
        ...prev,
        [type]: prev[type].includes(value) ? prev[type].filter((item) => item !== value) : [...prev[type], value]
      };

      // Clear downstream selections when upstream selection changes
      switch (type) {
        case 'zones':
          return {
            ...newSelection,
            states: [],
            cities: [],
            stores: []
          };
        case 'states':
          return {
            ...newSelection,
            cities: [],
            stores: []
          };
        case 'cities':
          return {
            ...newSelection,
            stores: []
          };
        default:
          return newSelection;
      }
    });
  };

  const removeSelection = (type, value) => {
    handleSelect(type, value);
  };

  const SelectionChips = ({ type, items }) => (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        justifyContent: { xs: 'center', sm: 'flex-start' }
      }}
    >
      {items.map((item) => (
        <Chip
          key={item}
          label={item}
          size={isMobile ? 'small' : 'medium'}
          onDelete={() => removeSelection(type, item)}
          sx={{
            bgcolor: 'primary.light',
            color: 'black',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            height: { xs: '24px', sm: '32px' },
            '& .MuiChip-deleteIcon': {
              color: 'black',
              width: { xs: '16px', sm: '22px' },
              height: { xs: '16px', sm: '22px' },
              '&:hover': {
                color: 'primary.main'
              }
            }
          }}
        />
      ))}
    </Box>
  );

  const SelectionBox = ({ title, type, disabled = false }) => {
    const searchInputRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused] = useState(false);
    const availableItems = getAvailableItems(type);
    const filteredItems = availableItems.filter((item) => item.toLowerCase().includes(searchTerm.toLowerCase()));

    // Keep focus on input when typing
    useEffect(() => {
      if (isFocused && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [searchTerm, isFocused]);

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: { xs: '100%', sm: 250 },
          p: { xs: 1.5, sm: 2 },
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          opacity: disabled ? 0.5 : 1,
          mb: { xs: 2, sm: 0 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 1.5, sm: 2 }
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
          >
            {title}
          </Typography>
          <LocationOnIcon
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          />
        </Box>

        <Box sx={{ position: 'relative', mb: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: '2px 8px',
              height: { xs: '36px', sm: '40px' }
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            <input
              ref={searchInputRef}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '14px',
                color: 'inherit',
                height: '100%'
              }}
              type="text"
              placeholder={disabled ? `Select previous level first` : `Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
            />
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: { xs: 150, sm: 200 },
            overflow: 'auto',
            mb: { xs: 1.5, sm: 2 }
          }}
        >
          {filteredItems.map((item) => (
            <FormControlLabel
              key={item}
              control={
                <Checkbox
                  checked={selection[type].includes(item)}
                  onChange={() => handleSelect(type, item)}
                  disabled={disabled}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={<Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{item}</Typography>}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mx: 0,
                my: { xs: 0.3, sm: 0.5 },
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }
              }}
            />
          ))}
        </Box>

        {selection[type].length > 0 && <SelectionChips type={type} items={selection[type]} />}
      </Box>
    );
  };

  const MobileSelect = () => {
    // Track which dropdown is open
    const [openDropdown, setOpenDropdown] = useState(null);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        {['zones', 'states', 'cities', 'stores'].map((type) => {
          const availableItems = getAvailableItems(type);
          const disabled = type !== 'zones' && getAvailableItems(type).length === 0;

          return (
            <FormControl key={type} fullWidth disabled={disabled} size="small" sx={{ mb: 1 }}>
              <InputLabel
                sx={{ fontSize: '0.85rem' }}
                onClick={() => setOpenDropdown(openDropdown === type ? null : type)}
                style={{ cursor: 'pointer' }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </InputLabel>
              <Select
                multiple
                open={openDropdown === type}
                onOpen={() => setOpenDropdown(type)}
                onClose={() => setOpenDropdown(null)}
                value={selection[type]}
                // ⛔️ Remove onChange completely
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          removeSelection(type, value);
                        }}
                        sx={{
                          m: '2px',
                          height: '24px',
                          fontSize: '0.75rem',
                          color: 'black'
                        }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 250 }
                  },
                  disableAutoFocus: true,
                  disableEnforceFocus: true,
                  MenuListProps: {
                    onMouseDown: (e) => e.stopPropagation(),
                    onClick: (e) => e.stopPropagation()
                  }
                }}
              >
                {availableItems.map((item) => (
                  <MenuItem
                    key={item}
                    value={item}
                    sx={{ fontSize: '0.85rem' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const newValues = selection[type].includes(item)
                        ? selection[type].filter((val) => val !== item)
                        : [...selection[type], item];

                      setSelection((prev) => {
                        switch (type) {
                          case 'zones':
                            return { ...prev, [type]: newValues, states: [], cities: [], stores: [] };
                          case 'states':
                            return { ...prev, [type]: newValues, cities: [], stores: [] };
                          case 'cities':
                            return { ...prev, [type]: newValues, stores: [] };
                          default:
                            return { ...prev, [type]: newValues };
                        }
                      });

                      // keep dropdown open
                      setOpenDropdown(type);
                    }}
                  >
                    <Checkbox checked={selection[type].includes(item)} />
                    <ListItemText primary={item} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      <Typography
        variant="h6"
        sx={{
          mb: { xs: 1.5, sm: 2, md: 3 },
          color: 'text.primary',
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        Location Selection
      </Typography>

      {isMobile ? (
        <MobileSelect />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: isTablet ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))'
            },
            gap: { xs: 1, sm: 1.5, md: 2 }
          }}
        >
          <SelectionBox title="Zones" type="zones" />
          <SelectionBox title="States" type="states" disabled={selection.zones.length === 0} />
          <SelectionBox title="Cities" type="cities" disabled={selection.states.length === 0} />
          <SelectionBox title="Stores" type="stores" disabled={selection.cities.length === 0} />
        </Box>
      )}

      <Box
        sx={{
          mt: { xs: 2, sm: 2.5, md: 3 },
          p: { xs: 1, sm: 1.5 },
          bgcolor: 'background.paper',
          color: 'black',
          borderRadius: 1,
          boxShadow: 1
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            mb: { xs: 1, sm: 1.5, md: 2 },
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
            textAlign: 'left' // changed
          }}
        >
          Selected Locations
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5, md: 2 } }}>
          {Object.entries(selection).map(
            ([type, items]) =>
              items.length > 0 && (
                <Box
                  key={type}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'flex-start', // changed
                    gap: { xs: 0.5, sm: 1 }
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                      minWidth: { sm: '60px' },
                      textAlign: 'left' // added or changed
                    }}
                  >
                    {type}:
                  </Typography>
                  <SelectionChips type={type} items={items} color="black" />
                </Box>
              )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LocationSelector;
